import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { projectAPI, modelAPI, jobAPI } from '../lib/api';
import { toast } from 'sonner';
import {
  FolderKanban, Cpu, Play, Plus, ArrowRight, Clock, CheckCircle2, XCircle, Loader2
} from 'lucide-react';

const statusIcons = {
  pending: <Clock className="w-4 h-4" />,
  queued: <Clock className="w-4 h-4" />,
  running: <Loader2 className="w-4 h-4 animate-spin" />,
  succeeded: <CheckCircle2 className="w-4 h-4" />,
  failed: <XCircle className="w-4 h-4" />,
  cancelled: <XCircle className="w-4 h-4" />,
};

const statusColors = {
  pending: 'warning',
  queued: 'warning',
  running: 'info',
  succeeded: 'success',
  failed: 'destructive',
  cancelled: 'secondary',
};

export default function ClientDashboard() {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [models, setModels] = useState([]);
  const [recentJobs, setRecentJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      const [projectsRes, modelsRes] = await Promise.all([
        projectAPI.list(),
        modelAPI.list(),
      ]);
      
      setProjects(projectsRes.data);
      setModels(modelsRes.data);

      // Get recent jobs from first project if exists
      if (projectsRes.data.length > 0) {
        const jobsRes = await jobAPI.list(projectsRes.data[0].id);
        setRecentJobs(jobsRes.data.slice(0, 5));
      }
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-8" data-testid="client-dashboard">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold tracking-tight font-heading">
            Welcome back, {user?.name?.split(' ')[0]}
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage your fine-tuning projects and models
          </p>
        </div>

        {/* Stats Grid - Bento Style */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Projects</CardTitle>
              <FolderKanban className="w-4 h-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{projects.length}</div>
              <p className="text-xs text-muted-foreground">
                of {user?.max_projects} max
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Fine-tuned Models</CardTitle>
              <Cpu className="w-4 h-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{models.length}</div>
              <p className="text-xs text-muted-foreground">
                Ready for use
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Total Jobs</CardTitle>
              <Play className="w-4 h-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {projects.reduce((acc, p) => acc + (p.job_count || 0), 0)}
              </div>
              <p className="text-xs text-muted-foreground">
                of {user?.max_jobs} max
              </p>
            </CardContent>
          </Card>

          <Card className="bg-zinc-900 text-white">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-zinc-300">Quick Start</CardTitle>
            </CardHeader>
            <CardContent>
              <Link to="/projects">
                <Button variant="secondary" size="sm" className="w-full" data-testid="create-project-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  New Project
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>

        {/* Projects & Jobs Grid */}
        <div className="grid gap-6 lg:grid-cols-2">
          {/* Recent Projects */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Recent Projects</CardTitle>
                  <CardDescription>Your active fine-tuning projects</CardDescription>
                </div>
                <Link to="/projects">
                  <Button variant="ghost" size="sm" data-testid="view-all-projects-btn">
                    View all <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                </Link>
              </div>
            </CardHeader>
            <CardContent>
              {projects.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <FolderKanban className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No projects yet</p>
                  <Link to="/projects">
                    <Button variant="link" size="sm">Create your first project</Button>
                  </Link>
                </div>
              ) : (
                <div className="space-y-3">
                  {projects.slice(0, 5).map((project) => (
                    <Link
                      key={project.id}
                      to={`/projects/${project.id}`}
                      className="block p-3 rounded-lg border border-border hover:border-zinc-400 transition-colors"
                      data-testid={`project-card-${project.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">{project.name}</p>
                          <p className="text-sm text-muted-foreground">
                            {project.job_count || 0} jobs · {project.model_count || 0} models
                          </p>
                        </div>
                        <ArrowRight className="w-4 h-4 text-muted-foreground" />
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Recent Jobs */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Recent Jobs</CardTitle>
                  <CardDescription>Latest fine-tuning tasks</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {recentJobs.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Play className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No jobs yet</p>
                  <p className="text-sm">Create a project and start training</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {recentJobs.map((job) => (
                    <div
                      key={job.id}
                      className="p-3 rounded-lg border border-border"
                      data-testid={`job-card-${job.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          {statusIcons[job.status]}
                          <div>
                            <p className="font-medium text-sm">
                              {job.name || job.base_model}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {job.vendor_code} · {job.base_model}
                            </p>
                          </div>
                        </div>
                        <Badge variant={statusColors[job.status]}>
                          {job.status}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Available Vendors */}
        <Card>
          <CardHeader>
            <CardTitle>Available Vendors</CardTitle>
            <CardDescription>Model providers you can use for fine-tuning</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {user?.allowed_vendors?.map((vendor) => (
                <Badge key={vendor} variant="outline" className="capitalize">
                  {vendor}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
